import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/meetings")({
  component: MeetingsPage,
});

import { useState, useEffect, useMemo } from "react";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { MeetingTable } from "@/components/crm/tables/MeetingTable";
import { MeetingFormSheet } from "@/components/crm/forms/MeetingFormSheet";
import {
  useFetchMeetings,
  useMeetingMutations,
  type MeetingOrdering,
} from "@/hooks/api/crm/useMeetings";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type { CrmMeeting, CrmMeetingPayload } from "@/types/crm";

function MeetingsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedTo, setAssignedTo] = useState<number | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        subject: SortDirection;
        status: SortDirection;
        start_datetime: SortDirection;
        location: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      MeetingOrdering
    >({
      subject: null,
      status: null,
      start_datetime: null,
      location: null,
      assigned_to__first_name: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<CrmMeeting | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState<CrmMeeting | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const meetingsQuery = useFetchMeetings({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const meetings = meetingsQuery.data?.results ?? [];
  const count = meetingsQuery.data?.count ?? 0;

  const { createMeeting, updateMeeting, deleteMeeting } = useMeetingMutations();

  const isMutating =
    createMeeting.isPending || updateMeeting.isPending || deleteMeeting.isPending;
  const isLoading = meetingsQuery.isPending || isMutating;
  const isFetching = meetingsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, assignedTo, ordering, setPage]);

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setStatusFilter("");
    setAssignedTo(undefined);
    setOrdering({
      subject: null,
      status: null,
      start_datetime: null,
      location: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmMeetingPayload) => {
    await createMeeting.mutateAsync(data);
    toast.success("Meeting scheduled!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: string, data: Partial<CrmMeetingPayload>) => {
    await updateMeeting.mutateAsync({ id, payload: data });
    toast.success("Meeting updated!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!meetingToDelete) return;
    try {
      await deleteMeeting.mutateAsync({ id: meetingToDelete.id });
      toast.success("Meeting deleted!");
      setIsDeleteDialogOpen(false);
      setMeetingToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Meetings"
          description="Schedule and manage meetings."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingMeeting(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Schedule Meeting"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search meetings..."
          />
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} className="w-[220px]" />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {["Planned", "Held", "Not Held"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FiltersPanel>

        <MeetingTable
          meetings={meetings}
          onView={(m) => {
            setEditingMeeting(m);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(m) => {
            setEditingMeeting(m);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(m) => {
            setMeetingToDelete(m);
            setIsDeleteDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <MeetingFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onEditClick={isViewOnly ? () => setIsViewOnly(false) : undefined}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          onEdit={onEdit}
          editingMeeting={editingMeeting}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Meeting"
          description={`Delete meeting "${meetingToDelete?.subject}"?`}
          itemName={meetingToDelete?.subject}
          isLoading={deleteMeeting.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
